import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { captureFunnelContext, trackFunnelEvent } from '../utils/jobFunnelAnalytics';

/** Worker-facing surfaces only — no click logging inside the admin app. */
const WORKER_PATH = /^\/(c1\/jobs-board|jobs-board|apply|login|signin|c1\/workers|[^/]+\/jobs-board)(\/|$)/;
const CLICKABLE = 'a,button,[role="button"],[role="tab"],[role="menuitem"],input[type="submit"],.MuiCheckbox-root,.MuiChip-clickable';
const MAX_ERRORS_PER_SESSION = 8;

const labelOf = (el: Element): string => {
  const explicit = el.getAttribute('data-track') || el.getAttribute('aria-label');
  const text = (explicit || (el as HTMLElement).innerText || el.getAttribute('title') || '').replace(/\s+/g, ' ').trim();
  return text.slice(0, 60);
};

/**
 * Global GA4 instrumentation for the worker-facing pages (Greg 2026-10-23):
 *  - `job_link_landed` once per tab on the first worker page, with via_invite
 *  - `ui_click` for every button / link / tab tap (label, tag, path, same-origin target)
 *  - `scroll_depth` (25/50/75/100), `first_interaction`, `element_seen` (shift list / Claim button / sticky bar),
 *    and a per-page `page_engagement` summary (max scroll %, seconds visible, taps, what was seen)
 *  - `js_error` for uncaught errors + unhandled rejections (a silent crash on the
 *    apply/claim path looks identical to "nobody came" without this)
 * Mount inside the Router, once.
 */
const InteractionTracker: React.FC = () => {
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const landedRef = useRef(false);
  const errorCount = useRef(0);

  useEffect(() => {
    if (landedRef.current || !WORKER_PATH.test(location.pathname)) return;
    landedRef.current = true;
    const ctx = captureFunnelContext(location.search);
    const ua = navigator.userAgent || '';
    trackFunnelEvent('job_link_landed', {
      referrer_host: (() => {
        try {
          return document.referrer ? new URL(document.referrer).host : 'direct';
        } catch {
          return 'unknown';
        }
      })(),
      in_app_browser: /FBAN|FBAV|Instagram|Line\/|Snapchat|GSA\//i.test(ua),
      platform: /iPhone|iPad/i.test(ua) ? 'ios' : /Android/i.test(ua) ? 'android' : 'desktop',
      lang: (navigator.language || '').slice(0, 5),
      utm_campaign: ctx.utm_campaign,
    });
  }, [location.pathname, location.search]);

  // Per-page engagement (2026-10-23): scroll depth, time to first interaction, and whether the shift list /
  // Claim button was ever ON SCREEN (elements tagged `data-track-seen`). Answers "did they even reach it?".
  useEffect(() => {
    if (!WORKER_PATH.test(location.pathname)) return undefined;
    const startedAt = Date.now();
    const fired = new Set<number>();
    const seen = new Set<string>();
    let maxPct = 0;
    let interactions = 0;
    let firstAt = 0;
    let visibleMs = 0;
    let visibleSince = document.visibilityState === 'visible' ? Date.now() : 0;
    let lastSentAt = 0;
    const secs = () => Math.round((Date.now() - startedAt) / 100) / 10;
    const pct = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      // A page that doesn't scroll yet (skeleton while loading) is 0%, not 100% — depth only counts real scrolling.
      return max <= 0 ? 0 : Math.min(100, Math.round((window.scrollY / max) * 100));
    };
    const interact = (kind: string) => {
      interactions += 1;
      if (!firstAt) {
        firstAt = Date.now();
        trackFunnelEvent('first_interaction', { kind, seconds: secs() });
      }
    };
    const onScroll = () => {
      const p = pct();
      if (p > maxPct) maxPct = p;
      for (const t of [25, 50, 75, 100]) {
        if (p >= t && !fired.has(t)) {
          fired.add(t);
          trackFunnelEvent('scroll_depth', { depth: t, seconds: secs() });
        }
      }
      if (window.scrollY > 4) interact('scroll');
    };
    const onPointer = () => interact('tap');
    const onVisibility = () => {
      if (document.visibilityState === 'visible') visibleSince = Date.now();
      else {
        if (visibleSince) visibleMs += Date.now() - visibleSince;
        visibleSince = 0;
        send();
      }
    };
    // Cumulative summary; re-sent each time the tab is hidden / the page is left (GA4 keeps the latest per visit),
    // de-duplicated when pagehide + cleanup fire together.
    const send = () => {
      if (Date.now() - lastSentAt < 500) return;
      lastSentAt = Date.now();
      const liveMs = visibleSince ? Date.now() - visibleSince : 0;
      trackFunnelEvent('page_engagement', {
        max_scroll_pct: maxPct,
        seconds_visible: Math.round((visibleMs + liveMs) / 100) / 10,
        interactions,
        seen: Array.from(seen).join(','),
        page_height_px: document.documentElement.scrollHeight,
        viewport_h: window.innerHeight,
      });
    };
    let io: IntersectionObserver | null = null;
    const watched = new WeakSet<Element>();
    const watch = (el: Element) => {
      if (watched.has(el) || !io) return;
      watched.add(el);
      io.observe(el);
    };
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        (entries) => {
          for (const en of entries) {
            if (!en.isIntersecting) continue;
            const name = en.target.getAttribute('data-track-seen') || 'unknown';
            if (!seen.has(name)) {
              seen.add(name);
              trackFunnelEvent('element_seen', { element: name, seconds: secs() });
            }
            io?.unobserve(en.target);
          }
        },
        { threshold: 0.5 },
      );
      document.querySelectorAll('[data-track-seen]').forEach(watch);
    }
    const mo = new MutationObserver(() => document.querySelectorAll('[data-track-seen]').forEach(watch));
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pointerdown', onPointer, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', send);
    return () => {
      send();
      mo.disconnect();
      io?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', send);
    };
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      try {
        if (!WORKER_PATH.test(pathRef.current)) return;
        const target = e.target as Element | null;
        const el = target?.closest?.(CLICKABLE);
        if (!el) return;
        const anchor = el.closest('a') as HTMLAnchorElement | null;
        let dest: string | undefined;
        if (anchor?.href) {
          try {
            const u = new URL(anchor.href, window.location.origin);
            dest = u.origin === window.location.origin ? u.pathname : u.host;
          } catch {
            /* ignore */
          }
        }
        trackFunnelEvent('ui_click', {
          label: labelOf(el),
          tag: el.tagName.toLowerCase(),
          disabled: (el as HTMLButtonElement).disabled === true || el.getAttribute('aria-disabled') === 'true',
          dest,
        });
      } catch {
        /* never break a click */
      }
    };
    const report = (kind: string, message: string, source?: string) => {
      if (!WORKER_PATH.test(pathRef.current) || errorCount.current >= MAX_ERRORS_PER_SESSION) return;
      errorCount.current += 1;
      trackFunnelEvent('js_error', { kind, message: message.slice(0, 100), source: source?.slice(-60) });
    };
    const onError = (ev: ErrorEvent) => report('error', ev.message || 'error', ev.filename);
    const onRejection = (ev: PromiseRejectionEvent) => {
      const r: any = ev.reason;
      report('unhandledrejection', String(r?.message || r || 'rejection'));
    };
    document.addEventListener('click', onClick, true);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
};

export default InteractionTracker;
